import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import type { Recent, IRecentsResult } from "app/custom-hooks/useRecentsHook";
import type { RecentsTypes } from "app/services/solutions2Services/HomepageDataFetchers/HomepageDataFetcherTypes";
import { getRecentsAnalysis } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import type { serializeItems } from "app/services/solutions2Services/HomepageDataFetchers/utils";

interface IRecentQuery<S = Recent> extends IRecentsResult<S> {
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<S[]>>;
}

export const useRecentQuery = <T = Recent>(
    filter?: RecentsTypes | RecentsTypes[],
    dedupe = false,
    dedupeFunction?: typeof serializeItems,
): IRecentQuery<T> => {
    const filterDep = Array.isArray(filter) ? filter.join() : filter;

    const {
        data: recents = [],
        isLoading: isRecentLoading,
        refetch,
    } = useQuery({
        queryKey: ["recents", filterDep, dedupe],
        queryFn: async () => {
            const recentItems: T[] = (await getRecentsAnalysis(filter, dedupe, dedupeFunction)) as T[];
            return recentItems ?? [];
        },
    });

    return {
        refetch,
        recents,
        isRecentLoading,
    };
};
